/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : FileUploadDeloitteFormate.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { HandleApiActions, profileActions } from "../../redux/actions";

const DEFAULT_MAX_SIZE = 800 * 1024;

function FileUploadDeloitteFormate({
  value,
  onChange,
  allowedExtensions = [],
  maxSize = DEFAULT_MAX_SIZE,
  label = "Choose File",
  helperText,
  disabled = false,
}) {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [fileState, setFileState] = useState(value || null);

  useEffect(() => {
    setFileState(value || null);
  }, [value]);

  const validateFile = (file) => {
    if (!file) return "Please select a file";

    const extension = file.name?.split(".").pop()?.toLowerCase();
    if (!allowedExtensions.includes(extension)) {
      return `Please select a valid file (${allowedExtensions
        .map((ext) => ext.toUpperCase())
        .join(" or ")})`;
    }

    if (file.size > maxSize) {
      return `File size should not exceed ${Math.round(maxSize / 1024)}KB`;
    }

    return null;
  };

  const resolvedHelperText =
    Array.isArray(allowedExtensions) && allowedExtensions.length > 0
      ? `${allowedExtensions.join(", ")}. Max size of ${Math.round(
        maxSize / 1024,
      )}KB`
      : helperText;

  const uploadFile = async (file) => {
    const validationError = validateFile(file);
    if (validationError) {
      toast.dark(validationError, {
        type: "error",
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setFileState({
      name: file.name,
      progress: 10,
      uploading: true,
      uploaded: false,
      previewUrl: localPreview,
    });

    try {
      const res = await dispatch(
        HandleApiActions({
          ...profileActions.upload_profile_pic,
          show_toast: false,
          file,
          is_file: true,
        }),
      );

      if (res?.error) {
        throw new Error(res?.message || "Upload failed");
      }

      const uploadedFile = {
        name: file.name,
        filename: res?.file_name || file.name,
        url: res?.file_url || res?.file_path || null,
        size: file.size,
        type: file.type,
      };

      setFileState({
        ...uploadedFile,
        previewUrl: localPreview,
        progress: 100,
        uploading: false,
        uploaded: true,
      });

      if (onChange) {
        onChange(uploadedFile);
      }
    } catch (error) {
      setFileState({
        name: file.name,
        progress: 0,
        uploading: false,
        uploaded: false,
        error: error?.message || "Upload failed",
      });

      toast.dark(error?.message || "Upload failed", {
        type: "error",
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleChooseFile = (file) => {
    if (!file) return;
    uploadFile(file);
  };

  const handleClear = () => {
    setFileState(null);
    if (onChange) onChange(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <Box>
      <Typography
        sx={{
          mb: 0.5,
          fontSize: "14px",
          fontWeight: 600,
        }}
      >
        Upload file
      </Typography>

      <Typography
        sx={{
          mb: 0.5,
          fontSize: "12px",
          color: "#666",
        }}
      >
        {resolvedHelperText}
      </Typography>

      <input
        hidden
        type="file"
        ref={inputRef}
        accept={allowedExtensions.map((ext) => `.${ext}`).join(",")}
        onChange={(e) => handleChooseFile(e.target.files?.[0])}
      />

      <Box sx={{ display: "flex", justifyContent: "start", mt: 2 }}>
        <Button
          variant="outlined"
          component="span"
          size="small"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          sx={{
            backgroundColor: "#26890D",
            color: "white",
            textTransform: "none",
            borderRadius: "0px",
            border: "none",

            "&:hover": {
              backgroundColor: "#20730B",
              border: "none",
            },

            "&.Mui-disabled": {
              backgroundColor: "#A8D09E",
              color: "white",
              cursor: "not-allowed",
              border: "none",
            },
          }}
        >
          {label}
        </Button>
      </Box>

      {fileState && (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "6px",
              backgroundColor: "#f9f9f9",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                color: "#53565A",
                wordBreak: "break-word",
              }}
            >
              {fileState.name}
            </Typography>

            <IconButton size="small" onClick={handleClear}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {fileState.uploading && (
            <Box sx={{ width: "100%", marginBottom: "8px", mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={fileState.progress || 0}
                sx={{
                  height: "5px",
                  borderRadius: "0px",
                  backgroundColor: "#e0e0e0",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#26890D",
                    borderRadius: "0px",
                  },
                }}
              />
            </Box>
          )}

          {fileState.uploaded && (
            <Typography
              sx={{
                fontSize: "12px",
                color: "#26890D",
                mt: 0.5,
              }}
            >
              Successfully Uploaded
            </Typography>
          )}

          {fileState.error && (
            <Typography
              sx={{
                fontSize: "12px",
                color: "#d32f2f",
                mt: 0.5,
              }}
            >
              {fileState.error}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default FileUploadDeloitteFormate;
